/**
 * Gem effects by type (AS3-motivated, passive on-hit).
 * Effect params have a base value and then scale modestly by gem.level.
 *
 * | Gem    | Effect        | Params                                               |
 * |--------|---------------|------------------------------------------------------|
 * | Yellow | MANA_GAIN     | +0.3 * level mana on hit                             |
 * | Orange | SPLASH        | 14% chance, AoE radius 1 cell (Chebyshev), full dmg  |
 * | Red    | CRIT          | 18% chance, ×2 damage (applied at shot-create time)  |
 * | Purple | SLOW          | 30% slow for 2000ms                                  |
 * | Green  | POISON        | 8 * level dps for 2500ms                             |
 * | Blue   | ARMOR_PIERCE  | -0.18 * level armor permanently (floor 0)            |
 * | Cyan   | SHOCK         | 17% chance, stun for 1500ms                          |
 * | Lime   | CHAIN         | 25% chance to hit 2nd target in radius at 60% dmg    |
 */

import type { GameSession } from "./session.js";
import type { GemMods, Monster, Tower } from "./entities.js";
import type { GemType } from "./types.js";
import { addMana, damageMonster, TRAP_EFFECT_MULT } from "./session.js";
import { TOWER_RADIUS_CELLS } from "./tower-shooting.js";

// --- Effect constants (balance-safe, not level-scaled) ---

export const YELLOW_MANA_PER_LEVEL = 0.3;

export const ORANGE_SPLASH_CHANCE = 0.14;
export const ORANGE_SPLASH_RADIUS = 1;

export const RED_CRIT_CHANCE = 0.18;
export const RED_CRIT_MULT = 2;

export const PURPLE_SLOW_RATIO = 0.30;
export const PURPLE_SLOW_MS = 2000;

export const GREEN_POISON_DPS_PER_LEVEL = 8;
export const GREEN_POISON_MS = 2500;

export const BLUE_ARMOR_PIERCE_PER_LEVEL = 0.18;

export const CYAN_SHOCK_CHANCE = 0.17;
export const CYAN_SHOCK_MS = 1500;

export const LIME_CHAIN_CHANCE = 0.25;
export const LIME_CHAIN_DMG_MULT = 0.60;

export function effectChanceForLevel(base: number, level: number): number {
  return Math.min(0.95, base * (1 + 0.12 * (level - 1)));
}

export function effectMagnitudeForLevel(level: number): number {
  return 1 + 0.08 * (level - 1);
}

export function effectDurationForLevel(level: number): number {
  return 1 + 0.12 * (level - 1);
}

// --- RNG (mulberry32, injected via session.rngSeed for determinism) ---

/** Returns [0,1). Mutates session.rngSeed. */
export function nextRandom(session: GameSession): number {
  let s = (session.rngSeed = ((session.rngSeed ?? 1) + 0x6d2b79f5) >>> 0);
  s = Math.imul(s ^ (s >>> 15), s | 1);
  s ^= s + Math.imul(s ^ (s >>> 7), s | 61);
  return ((s ^ (s >>> 14)) >>> 0) / 4294967296;
}

function percentMult(value: number): number {
  return 1 + 0.01 * value;
}

function clampChance(value: number): number {
  return Math.min(0.95, Math.max(0, value));
}

// --- CRIT: applied at shot-create time so the damage payload in the Shot is correct ---

/** Roll crit for Red gems; returns damage multiplier. */
export function rollCritMultiplier(gemType: GemType, session: GameSession): number {
  if (gemType !== "Red") return 1;
  const skillMult = percentMult(session.skillEffects.bloodboundMastery);
  const critChance = clampChance(RED_CRIT_CHANCE * skillMult);
  return nextRandom(session) < critChance ? RED_CRIT_MULT * skillMult : 1;
}

// --- Chebyshev distance helper ---

function cheb(
  a: { row: number; col: number },
  b: { row: number; col: number }
): number {
  return Math.max(Math.abs(a.row - b.row), Math.abs(a.col - b.col));
}

// --- Main entry: applied when a Shot hits its target ---

export interface ApplyEffectArgs {
  session: GameSession;
  tower: Tower;
  target: Monster | null; // null if target died/despawned before shot arrived
  hitPos: { row: number; col: number };
  baseDamage: number; // damage already dealt to primary target (for chain/splash scaling)
  gemType: GemType;
  gemLevel: number;
  /** Dual-type: secondary gem type to also apply on hit. Absent → single-type behaviour. */
  secondaryGemType?: GemType;
  /** Combine mods (dmgMult, fireRateMult, primaryEffectMult, etc.). */
  mods?: GemMods;
}

/**
 * Apply one gem type's passive on-hit effect, scaled by chanceMult and magnitudeMult.
 * For Trap towers, TRAP_EFFECT_MULT is applied on top (multiplication commutes).
 * MVP note: secondary Red does NOT grant crit — crit is rolled at shot-create time
 * from the primary type only; secondary Red is effectively a no-op here.
 */
function applyTypeEffect(args: {
  session: GameSession;
  tower: Tower;
  target: Monster | null;
  hitPos: { row: number; col: number };
  baseDamage: number;
  gemType: GemType;
  gemLevel: number;
  chanceMult: number;
  magnitudeMult: number;
}): void {
  const { session, tower, target, hitPos, baseDamage, gemType, gemLevel, chanceMult, magnitudeMult } = args;
  const isTrap = tower.type === "Trap";
  const trapEffectMult = isTrap ? TRAP_EFFECT_MULT * percentMult(session.skillEffects.traps) : 1;

  switch (gemType) {
    case "Yellow": {
      addMana(
        session,
        YELLOW_MANA_PER_LEVEL *
          gemLevel *
          effectMagnitudeForLevel(gemLevel) *
          magnitudeMult *
          percentMult(session.skillEffects.manaGatheringMastery)
      );
      return;
    }
    case "Orange": {
      const splashSkillMult = percentMult(session.skillEffects.multipleDamageMastery);
      const explosionSkillMult = percentMult(session.skillEffects.violentExplosions);
      const splashChance = clampChance(
        effectChanceForLevel(ORANGE_SPLASH_CHANCE, gemLevel) *
        trapEffectMult *
        chanceMult *
        splashSkillMult
      );
      if (nextRandom(session) >= splashChance) return;
      // AoE around hitPos, excluding the primary target (already damaged).
      for (const m of session.monsters) {
        if (target && m.id === target.id) continue;
        if (cheb(m.position, hitPos) <= ORANGE_SPLASH_RADIUS) {
          damageMonster(session, m.id, baseDamage * magnitudeMult * splashSkillMult * explosionSkillMult);
        }
      }
      return;
    }
    case "Red": {
      // Crit already rolled at shot creation for primary; secondary Red is a no-op in MVP.
      return;
    }
    case "Purple": {
      if (!target) return;
      const slowSkillMult = percentMult(session.skillEffects.slowMastery);
      const slowRatio = Math.min(0.75, PURPLE_SLOW_RATIO * effectMagnitudeForLevel(gemLevel) * magnitudeMult * slowSkillMult);
      const slowMs = PURPLE_SLOW_MS * effectDurationForLevel(gemLevel) * trapEffectMult * magnitudeMult * slowSkillMult;
      target.slowRatio = Math.max(target.slowRatio ?? 0, slowRatio);
      target.slowUntilMs = Math.max(
        target.slowUntilMs ?? 0,
        session.elapsedMs + slowMs
      );
      return;
    }
    case "Green": {
      if (!target) return;
      const poisonSkillMult = percentMult(session.skillEffects.poisonMastery);
      const poisonDps = GREEN_POISON_DPS_PER_LEVEL * gemLevel * effectMagnitudeForLevel(gemLevel) * trapEffectMult * magnitudeMult * poisonSkillMult;
      const poisonMs = GREEN_POISON_MS * effectDurationForLevel(gemLevel) * trapEffectMult * magnitudeMult * poisonSkillMult;
      target.poisonDps = Math.max(target.poisonDps ?? 0, poisonDps);
      target.poisonUntilMs = Math.max(
        target.poisonUntilMs ?? 0,
        session.elapsedMs + poisonMs
      );
      return;
    }
    case "Blue": {
      if (!target) return;
      const pierce = BLUE_ARMOR_PIERCE_PER_LEVEL * gemLevel * effectMagnitudeForLevel(gemLevel) * trapEffectMult * magnitudeMult * percentMult(session.skillEffects.armorTearingMastery);
      target.armor = Math.max(0, target.armor - pierce);
      return;
    }
    case "Cyan": {
      if (!target) return;
      const shockSkillMult = percentMult(session.skillEffects.shockingMastery);
      const shockChance = clampChance(effectChanceForLevel(CYAN_SHOCK_CHANCE, gemLevel) * trapEffectMult * chanceMult * shockSkillMult);
      if (nextRandom(session) >= shockChance) return;
      const shockMs = CYAN_SHOCK_MS * effectDurationForLevel(gemLevel) * trapEffectMult * magnitudeMult * shockSkillMult;
      target.shockUntilMs = Math.max(
        target.shockUntilMs ?? 0,
        session.elapsedMs + shockMs
      );
      return;
    }
    case "Lime": {
      const chainSkillMult = percentMult(session.skillEffects.chainHitMastery);
      const chainChance = clampChance(effectChanceForLevel(LIME_CHAIN_CHANCE, gemLevel) * trapEffectMult * chanceMult * chainSkillMult);
      if (nextRandom(session) >= chainChance) return;
      // Pick nearest other monster in tower radius.
      let best: Monster | null = null;
      let bestDist = Infinity;
      for (const m of session.monsters) {
        if (target && m.id === target.id) continue;
        const d = cheb(tower.cell, m.position);
        if (d <= TOWER_RADIUS_CELLS && d < bestDist) {
          best = m;
          bestDist = d;
        }
      }
      if (best) {
        damageMonster(session, best.id, baseDamage * LIME_CHAIN_DMG_MULT * effectMagnitudeForLevel(gemLevel) * magnitudeMult * chainSkillMult);
      }
      return;
    }
  }
}

/**
 * Apply passive on-hit effect. Primary damage has already been applied upstream.
 * `target` may be null if it died before impact — most effects still fizzle gracefully.
 * For Trap towers, spec-effect chances/magnitudes are multiplied by TRAP_EFFECT_MULT (+80%).
 * For combined gems, mods control scaling of primary and secondary effects.
 */
export function applyGemEffectOnHit(args: ApplyEffectArgs): void {
  const { session, tower, target, hitPos, baseDamage, gemType, gemLevel, secondaryGemType, mods } = args;
  const dualSkillMult = secondaryGemType === undefined ? 1 : percentMult(session.skillEffects.dualGemMastery);

  // Primary effect.
  const primaryMult = (mods?.primaryEffectMult ?? 1) * dualSkillMult;
  applyTypeEffect({ session, tower, target, hitPos, baseDamage, gemType, gemLevel, chanceMult: primaryMult, magnitudeMult: primaryMult });

  // Secondary effect (only if dual-type gem).
  if (secondaryGemType !== undefined) {
    if (mods?.secondaryProcChance !== undefined) {
      // Case 3 (hybrid combine): probabilistic secondary proc.
      if (nextRandom(session) >= mods.secondaryProcChance) return;
      const secMult = (mods.secondaryEffectMult ?? 1) * dualSkillMult;
      applyTypeEffect({ session, tower, target, hitPos, baseDamage, gemType: secondaryGemType, gemLevel, chanceMult: 1, magnitudeMult: secMult });
    } else {
      // Case 2 (dual combine): secondary always fires, scaled by secondaryEffectMult.
      const secMult = (mods?.secondaryEffectMult ?? 1) * dualSkillMult;
      applyTypeEffect({ session, tower, target, hitPos, baseDamage, gemType: secondaryGemType, gemLevel, chanceMult: secMult, magnitudeMult: secMult });
    }
  }
}

// --- Poison DoT tick ---

/** Apply active DoT damage to all poisoned monsters. Called each game tick. */
export function tickMonsterDots(session: GameSession, deltaMs: number): void {
  if (deltaMs <= 0) return;
  // Iterate over a copy — damageMonster may remove entries.
  const monsters = [...session.monsters];
  for (const m of monsters) {
    if ((m.poisonUntilMs ?? 0) <= session.elapsedMs) continue;
    const dps = m.poisonDps ?? 0;
    if (dps <= 0) continue;
    damageMonster(session, m.id, dps * deltaMs / 1000);
  }
}
